import polygonClipping from 'polygon-clipping';
import { createRawParcelBoundarySnapshot } from '../../lib/generationImpact/boundaryAuthority';
import { polygonArea } from '../../lib/siteBoundaryGeometry';
import { interpretAqueduct4Annual } from '../../lib/aqueduct4Screening';
import type { Aqueduct4StudyAssessment } from '../../types/aqueduct4';
import type { RefdataFeature, RefdataResultMeta } from '../../types/refdata';
import { isPrivateRefdataAcceptanceDataset, refdataBbox, refdataContains } from '../refdata/refdataClient';
import { AQUEDUCT4_SOURCE_LABEL, AQUEDUCT4_SOURCE_URL, isAqueduct4Feature, isAqueduct4SourceClosure } from '../refdata/aqueduct4Contract';
import type { ProviderGeoContext, ProviderResult } from './types';
import type { WaterStressData } from './wriAqueduct';

type Multi = polygonClipping.MultiPolygon;
const qualifications = [
  'Historical basin/province/aquifer screening; not measured site supply, water allocation or water rights.',
  'Baseline water stress represents 1979–2019. Other composite indicators retain their own older source periods.',
  'The default overall water-risk composite is independent of baseline stress; missing indicator weights remain disclosed.',
];

function area(multi: Multi): number {
  return multi.reduce((sum, polygon) => sum + polygon.reduce((subtotal, ring, index) =>
    subtotal + (index === 0 ? 1 : -1) * polygonArea(ring.map(([lng, lat]) => ({ lat, lng }))), 0), 0);
}

function union(geometries: Multi[]): Multi {
  return geometries.length ? polygonClipping.union(geometries[0]!, ...geometries.slice(1)) : [];
}

function requireSource(result: RefdataResultMeta, expectedSource?: { version: string; compilerHash: string }): void {
  if (!isAqueduct4SourceClosure(result.aqueduct4Sources, result.version) || (expectedSource && (result.version !== expectedSource.version || result.aqueduct4Sources?.compilerReceipt.sha256 !== expectedSource.compilerHash))) {
    throw new Error('Aqueduct 4 query source is unbound or changed during the study');
  }
}

export async function fetchAqueduct4Screening(lat: number, lng: number, geo?: ProviderGeoContext): Promise<ProviderResult<WaterStressData> | null> {
  geo?.signal?.throwIfAborted();
  const parcels = geo?.boundaries?.length ? geo.boundaries : geo?.boundary && geo.boundary.length >= 3 ? [{ id: 'boundary', polygon: geo.boundary }] : [];
  if (parcels.length > 64) throw new Error('Aqueduct study exceeds the supported parcel count');
  if (new Set(parcels.map(parcel => parcel.id)).size !== parcels.length || parcels.some(parcel => typeof parcel.id !== 'string' || !parcel.id.trim())) {
    throw new Error('Aqueduct study needs distinct parcel identities');
  }
  const observations: Aqueduct4StudyAssessment['observations'] = [];
  const reasons: string[] = [];
  const invalidStudyParcelIds: string[] = [];
  let version: string | undefined;
  let expectedSource: { version: string; compilerHash: string } | undefined;
  let unknown = false; let truncated = false;
  let baselineUnavailable = false; let overallUnavailable = false;
  let requestedArea = 0; let knownArea = 0; let baselineArea = 0; let overallArea = 0;
  const requestedGeometries: Multi[] = []; const allKnown: Multi[] = []; const allBaseline: Multi[] = []; const allOverall: Multi[] = [];
  const inspect = (feature: RefdataFeature) => {
    if (!isAqueduct4Feature(feature)) throw new Error('Malformed Aqueduct 4 candidate feature');
    return feature.props.wri4GeometryStatus === 'native-valid' ? interpretAqueduct4Annual(feature.props) : null;
  };
  if (!parcels.length) {
    const result = await refdataContains('wri-aqueduct', { lat, lng });
    geo?.signal?.throwIfAborted();
    if (!result) {
      if (isPrivateRefdataAcceptanceDataset('wri-aqueduct')) throw new Error('Private Aqueduct source unavailable; legacy fallback withheld');
      return null;
    }
    requireSource(result); version = result.version; truncated = result.truncated !== false;
    const seen = new Set<string>();
    for (const feature of result.features) {
      if (seen.has(feature.id)) continue;
      seen.add(feature.id);
      const observation = inspect(feature);
      if (observation) observations.push(observation); else unknown = true;
    }
    requestedArea = 1;
    knownArea = observations.length ? 1 : 0;
    baselineArea = observations.length && observations.every(row => row.baselineStress.status !== 'unavailable') ? 1 : 0;
    overallArea = observations.length && observations.every(row => row.overallWaterRisk.status !== 'unavailable') ? 1 : 0;
  } else {
    for (const parcel of parcels) {
      geo?.signal?.throwIfAborted();
      try {
        // Validate with the app's existing boundary authority before clipping;
        // polygon-clipping alone can silently normalize a self-intersecting ring.
        // Use the original coordinates below, never its canonicalized output.
        createRawParcelBoundarySnapshot({ siteBoundary: parcel.polygon });
      } catch (error) {
        invalidStudyParcelIds.push(parcel.id);
        reasons.push(`Study parcel ${parcel.id} has invalid geometry: ${(error as Error).message}. Its footprint and aggregate score remain unknown; edit the map boundary.`);
        continue;
      }
      const query: Multi = [[parcel.polygon.map(point => [point.lng, point.lat])]];
      const total = area(query);
      if (!(total > 0)) throw new Error('Aqueduct study polygon has no area');
      const result = await refdataBbox('wri-aqueduct', {
        minLat: Math.min(...parcel.polygon.map(p => p.lat)), maxLat: Math.max(...parcel.polygon.map(p => p.lat)),
        minLng: Math.min(...parcel.polygon.map(p => p.lng)), maxLng: Math.max(...parcel.polygon.map(p => p.lng)),
      }, 2000);
      geo?.signal?.throwIfAborted();
      if (!result) {
        if (version || isPrivateRefdataAcceptanceDataset('wri-aqueduct')) throw new Error('Aqueduct source unavailable midway through study; legacy fallback withheld');
        return null;
      }
      requireSource(result, expectedSource); version = result.version;
      expectedSource ??= { version, compilerHash: result.aqueduct4Sources!.compilerReceipt.sha256 }; truncated ||= result.truncated !== false;
      const known: Multi[] = []; const baseline: Multi[] = []; const overall: Multi[] = [];
      const grouped = new Map<string, { observation: NonNullable<ReturnType<typeof inspect>>; geoms: Multi[] }>();
      for (const feature of result.features) {
        const observation = inspect(feature);
        const intersection = polygonClipping.intersection(feature.geom.coordinates as Multi, query);
        if (area(intersection) <= 1e-9) continue;
        if (!observation) { unknown = true; continue; }
        known.push(intersection);
        if (observation.baselineStress.status !== 'unavailable') baseline.push(intersection); else baselineUnavailable = true;
        if (observation.overallWaterRisk.status !== 'unavailable') overall.push(intersection); else overallUnavailable = true;
        const group = grouped.get(observation.stringId) ?? { observation, geoms: [] };
        group.geoms.push(intersection); grouped.set(observation.stringId, group);
      }
      for (const group of grouped.values()) observations.push({ ...group.observation, parcelId: parcel.id, intersectionAcres: area(union(group.geoms)) });
      requestedGeometries.push(query);
      allKnown.push(...known); allBaseline.push(...baseline); allOverall.push(...overall);
    }
    // Overlapping selected parcels count once for coverage, while observations
    // retain per-parcel identities for the user's study map.
    // Even an entirely invalid selection still resolves the declared source,
    // but never treats the point as the selected study footprint.
    if (!version) {
      const result = await refdataContains('wri-aqueduct', { lat, lng });
      geo?.signal?.throwIfAborted();
      if (!result) {
        if (isPrivateRefdataAcceptanceDataset('wri-aqueduct')) throw new Error('Private Aqueduct source unavailable; legacy fallback withheld');
        return null;
      }
      requireSource(result); version = result.version;
    }
    requestedArea = area(union(requestedGeometries));
    knownArea = area(union(allKnown)); baselineArea = area(union(allBaseline)); overallArea = area(union(allOverall));
  }
  const fraction = (value: number) => requestedArea > 0 ? Math.min(1, Math.max(0, value / requestedArea)) : 0;
  const coveredFraction = invalidStudyParcelIds.length ? null : fraction(knownArea);
  const baselineComplete = !invalidStudyParcelIds.length && !unknown && !truncated && !baselineUnavailable && fraction(baselineArea) >= 1 - 1e-6;
  const overallComplete = !invalidStudyParcelIds.length && !unknown && !truncated && !overallUnavailable && fraction(overallArea) >= 1 - 1e-6;
  if (unknown) reasons.push('An original invalid geometry has an uncertain component envelope intersecting the study; aggregate scores are withheld.');
  if (truncated) reasons.push('The query did not establish complete feature delivery; aggregate scores are withheld.');
  if (coveredFraction != null && coveredFraction < 1 - 1e-6) reasons.push('Part of the study lacks a valid covering model feature; uncovered area remains unknown.');
  if (!baselineComplete) reasons.push('Baseline stress is not established for the complete study footprint.');
  if (!overallComplete) reasons.push('Overall risk is not established for the complete study footprint.');
  if (observations.some(row => row.overallWaterRisk.status === 'assessed-partial-indicators')) reasons.push('Some overall-risk observations have incomplete indicator weights; inspect their retained available-weight fractions.');
  const baselineCandidates = observations.filter(row => row.baselineStress.score !== null).sort((a, b) => b.baselineStress.score! - a.baselineStress.score!);
  const overallCandidates = observations.filter(row => row.overallWaterRisk.score !== null).sort((a, b) => b.overallWaterRisk.score! - a.overallWaterRisk.score!);
  const worst = baselineCandidates[0];
  const baselineValue = baselineComplete ? worst?.baselineStress : undefined;
  const overallValue = overallComplete ? overallCandidates[0]?.overallWaterRisk : undefined;
  // Positive high-concern evidence survives incomplete coverage. Missing or
  // partial low evidence cannot become a Low/Moderate aggregate for the site.
  const stressLevel = worst?.baselineStress.concern === 'High' ? 'High' : baselineValue?.concern ?? 'Unknown';
  const studyAssessment: Aqueduct4StudyAssessment = { version: version!, status: !observations.length ? 'unknown'
    : baselineComplete && overallComplete ? 'complete' : 'partial', queryScope: parcels.length ? 'study-polygons' : 'point',
    coveredFraction, unknownGeometryEncountered: unknown, invalidStudyParcelIds, reasons, observations };
  const data: WaterStressData = {
    sourceEdition: '4.0', bwsScore: baselineValue?.score ?? null, bwsCategory: baselineValue?.category ?? null,
    bwsLabel: baselineValue?.label ?? 'Not established for the complete study',
    bwsRaw: baselineValue?.withdrawalToSupplyRatio ?? null, overallWaterRisk: overallValue?.score ?? null,
    stressLevel, region: worst?.country ?? '', subRegion: worst?.province ?? '', studyAssessment,
    sourceQualification: [...qualifications, 'The source contains 12 empty geometries that cannot be located; model coverage is not a legal or water-supply determination.',
      'Aggregate scores use the highest known applicable basin score, retaining every observation and footprint coverage.', ...reasons],
  };
  return { data, cacheTtlMs: studyAssessment.status === 'complete' ? undefined : 0,
    sources: [{ field: 'waterStress', source: `${AQUEDUCT4_SOURCE_LABEL}. ${data.bwsLabel}. ${data.sourceQualification!.join(' ')}`,
      url: AQUEDUCT4_SOURCE_URL, retrievedAt: new Date().toISOString() }] };
}
